import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApi } from "../lib/api";
import { Badge, Button, Card, Empty, ErrorNote, inputCls, Loading, PageHeader, StickyBar, Table, Td } from "../components/ui";

export default function Contacts() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("");
  const [territory, setTerritory] = useState<string | null>(null); // null until we know whether the user has one
  const [signal, setSignal] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const params = new URLSearchParams({ q: debounced, territory: territory ?? "mine", ...(status ? { status } : {}), ...(signal ? { signal: "1" } : {}) });
  const { data, error, loading } = useApi<any>(`/contacts?${params}`, [debounced, status, territory, signal]);
  // Default to the rep's territory; users without one (e.g. the developer account) see all.
  useEffect(() => {
    if (territory === null && data) setTerritory(data.myTerritory ? "mine" : "all");
  }, [data, territory]);
  const hits: any[] = data?.hits ?? [];
  const selectable = hits.filter((c) => !c.optOut && !["Enrolled", "Active", "Paused"].includes(c.status ?? ""));
  const allOn = selectable.length > 0 && selectable.every((c) => selected.has(c.id));
  const toggle = (id: string, on: boolean) => setSelected((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });

  return (
    <div className="space-y-5">
      <PageHeader title="Contacts" sub={data ? `${data.matched} match · ${data.total} contacts at ${data.accounts} accounts` : undefined} />
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${inputCls} max-w-sm`} placeholder="Search name, email, title or account" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search contacts" />
        <select className={`${inputCls} w-52`} value={territory ?? "mine"} onChange={(e) => setTerritory(e.target.value)} aria-label="Territory">
          {data?.myTerritory && <option value="mine">My territory</option>}
          <option value="all">All territories</option>
          {(data?.territories ?? []).map((t: any) => <option key={t.id} value={t.id}>{t.ownerName}'s territory</option>)}
          <option value="unassigned">Unassigned</option>
        </select>
        <select className={`${inputCls} w-44`} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Any status</option>
          <option value="none">Not in a campaign</option>
          {["Enrolled", "Active", "Paused", "Replied", "Completed", "Bounced", "Do Not Contact"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={signal} onChange={(e) => setSignal(e.target.checked)} /> Has a fresh signal</label>
      </div>
      <ErrorNote error={error} />
      <Card pad={false}>
        <div className="p-5">
          {loading && !data ? (
            <Loading />
          ) : !hits.length ? (
            <Empty title="No contacts match">{data?.total === 0 ? "Import the Infor Outreach Console export (Admin → Settings → Data → Import)." : "Try another territory, status or search."}</Empty>
          ) : (
            <Table
              head={[
                <input key="all" type="checkbox" aria-label="Select all" checked={allOn} onChange={(e) => setSelected(e.target.checked ? new Set(selectable.map((c) => c.id)) : new Set())} />,
                "Contact",
                "Account",
                "State",
                "Territory",
                "Status",
              ]}
            >
              {hits.map((c) => {
                const canPick = selectable.includes(c);
                return (
                  <tr key={c.id}>
                    <Td><input type="checkbox" aria-label={`Select ${c.name}`} disabled={!canPick} checked={selected.has(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} /></Td>
                    <Td><Link className="font-medium text-accent-text hover:underline" to={`/contacts/${c.id}`}>{c.name}</Link><div className="text-xs text-muted">{c.title}</div></Td>
                    <Td><Link className="hover:underline" to={`/accounts/${c.accountId}`}>{c.accountName}</Link>{c.signal && <div className="text-xs text-good">fresh signal</div>}</Td>
                    <Td>{c.state ?? <span className="text-warn">none</span>}</Td>
                    <Td>{c.territoryOwner ?? <span className="text-warn">Unassigned</span>}</Td>
                    <Td>
                      {c.optOut ? <Badge tone="Do Not Contact">Do Not Contact</Badge> : c.status ? <Badge>{c.status}</Badge> : !c.active ? <Badge>inactive</Badge> : <span className="text-xs text-muted">not in a campaign</span>}
                      {c.noCalls && <div className="mt-0.5 text-xs text-muted">No calls</div>}
                    </Td>
                  </tr>
                );
              })}
            </Table>
          )}
        </div>
      </Card>
      {selected.size > 0 && (
        <StickyBar>
          <span className="mr-auto text-sm text-ink-2">{selected.size} selected</span>
          <Button onClick={() => setSelected(new Set())}>Clear</Button>
          <Button tone="primary" onClick={() => nav(`/campaigns/new?contacts=${[...selected].join(",")}`)}>Add to campaign</Button>
        </StickyBar>
      )}
    </div>
  );
}
